import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Scale,
  FileText,
  Users,
  Calendar,
  DollarSign,
  MessageSquare,
  Folder,
  LogOut,
  Menu,
  X,
  Clock,
  Briefcase,
  Table,
  Video,
  BookOpen,
  ClipboardList,
  Mail,
  Inbox,
  User,
  Settings,
} from 'lucide-react';
import { Logo } from '../components/Logo';
import NotificationBell from '../components/NotificationBell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LawyerDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setUser(JSON.parse(userStr));
    }
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const menuItems = [
    { name: 'اللوحة الرئيسية', path: '/dashboard', icon: Scale },
    { name: 'البريد الإلكتروني', path: '/emails/inbox', icon: Mail },
    { name: 'طلبات العملاء المحالة', path: '/employee/client-requests', icon: Inbox },
    { name: 'المهام المحالة إلي', path: '/my-tasks', icon: ClipboardList },
    { name: 'جداول العمل', path: '/work-schedules', icon: Table },
    { name: 'المكتبة القانونية', path: '/legal-library', icon: BookOpen },
    { name: 'جدول القضايا', path: '/cases-table', icon: Table },
    { name: 'القضايا', path: '/cases', icon: FileText },
    { name: 'المهام المحالة', path: '/assignments', icon: Briefcase },
    { name: 'المواعيد', path: '/appointments', icon: Calendar },
    { name: 'الاجتماعات', path: '/meetings', icon: Video },
    { name: 'اجتماعات الفيديو', path: '/video-meetings', icon: Video },
    { name: 'الحضور والانصراف', path: '/attendance', icon: Clock },
    { name: 'العملاء', path: '/clients', icon: Users },
    { name: 'الفواتير', path: '/invoices', icon: DollarSign },
    { name: 'الاستشارات', path: '/consultations', icon: MessageSquare },
    { name: 'المستندات', path: '/documents', icon: Folder },
    { name: 'إعدادات الحساب', path: '/account-settings', icon: User },
  ];

  return (
    <div className="min-h-screen bg-sand" dir="rtl" lang="ar">
      {/* Top Header Bar with Notification */}
      <div className="fixed top-0 left-0 right-0 lg:right-56 bg-primary/95 backdrop-blur-sm z-30 h-12 flex items-center justify-between px-4 border-b border-white/10">
        <div className="flex items-center gap-2">
          <span className="text-white/80 text-sm font-body">مرحباً، {user?.full_name}</span>
        </div>
        <NotificationBell />
      </div>

      {/* Mobile Menu Toggle */}
      <div className="lg:hidden fixed top-14 left-4 z-50">
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 bg-primary text-white rounded-md"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed right-0 top-0 h-full bg-primary w-56 transition-transform duration-300 z-40 overflow-y-auto ${
          sidebarOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <Logo size="sm" />
            <h1 className="text-base font-heading font-bold text-white">
              لوحة المحامي
            </h1>
          </div>

          <nav className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  data-testid={`menu-${item.path.substring(1)}`}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-white/80 hover:text-white hover:bg-white/10 rounded-md transition-all duration-200 font-body text-sm"
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.name}</span>
                </button>
              );
            })}
          </nav>

          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full mt-4 flex items-center gap-2 px-3 py-2 text-white/80 hover:text-white hover:bg-red-600 rounded-md transition-all duration-200 font-body text-sm"
          >
            <LogOut className="w-4 h-4" />
            <span>تسجيل الخروج</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:mr-56 p-6 pt-16">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h2 className="text-3xl font-heading font-bold text-primary mb-2">
              مرحبا، {user?.full_name}
            </h2>
            <p className="text-base text-gray-600 font-body">
              نظرة عامة على أعمالك اليوم
            </p>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100" data-testid="stat-total-cases">
                <div className="flex items-center justify-between mb-4">
                  <FileText className="w-8 h-8 text-gold" />
                  <span className="text-3xl font-bold text-primary font-heading">
                    {stats.total_cases}
                  </span>
                </div>
                <p className="text-sm text-gray-600 font-body">إجمالي القضايا</p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100" data-testid="stat-active-cases">
                <div className="flex items-center justify-between mb-4">
                  <FileText className="w-8 h-8 text-green-500" />
                  <span className="text-3xl font-bold text-primary font-heading">
                    {stats.active_cases}
                  </span>
                </div>
                <p className="text-sm text-gray-600 font-body">قضايا نشطة</p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100" data-testid="stat-total-clients">
                <div className="flex items-center justify-between mb-4">
                  <Users className="w-8 h-8 text-blue-500" />
                  <span className="text-3xl font-bold text-primary font-heading">
                    {stats.total_clients}
                  </span>
                </div>
                <p className="text-sm text-gray-600 font-body">إجمالي العملاء</p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100" data-testid="stat-pending-consultations">
                <div className="flex items-center justify-between mb-4">
                  <MessageSquare className="w-8 h-8 text-orange-500" />
                  <span className="text-3xl font-bold text-primary font-heading">
                    {stats.pending_consultations}
                  </span>
                </div>
                <p className="text-sm text-gray-600 font-body">استشارات معلقة</p>
              </div>
            </div>
          )}

          {/* Upcoming Appointments */}
          {stats?.upcoming_appointments && stats.upcoming_appointments.length > 0 && (
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
              <h3 className="text-xl font-heading font-semibold text-primary mb-4">
                المواعيد القادمة
              </h3>
              <div className="space-y-4">
                {stats.upcoming_appointments.map((apt) => (
                  <div
                    key={apt.id}
                    className="flex items-center justify-between p-4 bg-sand rounded-md"
                    data-testid={`appointment-${apt.id}`}
                  >
                    <div>
                      <p className="font-semibold text-primary font-body">{apt.title}</p>
                      <p className="text-sm text-gray-600 font-body">العميل: {apt.client_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600 font-body">
                        {new Date(apt.appointment_date).toLocaleDateString('ar-EG')}
                      </p>
                      <p className="text-xs text-gray-500 font-body">
                        {new Date(apt.appointment_date).toLocaleTimeString('ar-EG', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
